import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquareShare, Send, Mail, Loader2, CheckCircle2, AlertTriangle, Eye, ThumbsUp, BookOpen } from "lucide-react";

export default function ParentNotifications() {
  const [exams, setExams] = useState([]);
  const [examId, setExamId] = useState("");
  const [results, setResults] = useState([]);
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState([]);
  const [sending, setSending] = useState(false);
  const [statuses, setStatuses] = useState({}); // result_id -> sent|sending|error
  const [openId, setOpenId] = useState("");
  const [preview, setPreview] = useState({});
  const [previewLoading, setPreviewLoading] = useState("");

  const togglePreview = async (rid) => {
    if (openId === rid) { setOpenId(""); return; }
    setOpenId(rid);
    if (!preview[rid]) {
      setPreviewLoading(rid);
      try {
        const { data } = await api.get(`/results/${rid}/parent-preview`);
        setPreview((p) => ({ ...p, [rid]: data }));
      } catch (e) { toast.error(apiError(e)); setOpenId(""); }
      finally { setPreviewLoading(""); }
    }
  };

  useEffect(() => {
    api.get("/exams").then((r) => { setExams(r.data); if (r.data[0]) setExamId(r.data[0].id); });
    api.get("/students").then((r) => setStudents(r.data));
  }, []);

  const loadStatuses = (eid) =>
    api.get("/email-logs", { params: { exam_id: eid } }).then((r) => {
      const m = {};
      r.data.forEach((l) => { if (l.status === "success") m[l.result_id] = "sent"; else m[l.result_id] = "error"; });
      setStatuses(m);
    }).catch(() => {});

  useEffect(() => {
    if (!examId) return;
    api.get(`/exams/${examId}/results`).then((r) => setResults(r.data));
    loadStatuses(examId);
  }, [examId]);

  const parentOf = (sid) => students.find((s) => s.id === sid)?.parent_email || "";
  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const toggleAll = () => setSelected((s) => s.length === results.length ? [] : results.map((r) => r.id));

  const sendOne = async (rid) => {
    setStatuses((s) => ({ ...s, [rid]: "sending" }));
    try {
      const { data } = await api.post(`/results/${rid}/send-parent-email`);
      setStatuses((s) => ({ ...s, [rid]: "sent" }));
      toast.success(`Gönderildi: ${data.to}`);
    } catch (e) {
      setStatuses((s) => ({ ...s, [rid]: "error" }));
      toast.error(apiError(e));
    }
  };

  const sendSelected = async () => {
    if (!selected.length) { toast.error("En az bir öğrenci seçin"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/exams/${examId}/send-parent-emails`, { result_ids: selected });
      toast.success(`${data.sent} veliye e-posta gönderildi`);
      if (data.failed?.length) {
        toast.warning(`${data.failed.length} gönderim başarısız (örn. veli e-postası eksik)`);
        data.failed.slice(0, 3).forEach((f) => toast.error(f.error));
      }
      loadStatuses(examId);
      setSelected([]);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  return (
    <div>
      <PageHeader title="Veli Bildirimleri" desc="Sınav sonuç karnelerini velilere e-posta + PDF olarak gönderin">
        <Button onClick={sendSelected} disabled={sending} data-testid="send-notifications">
          {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Seçililere Gönder ({selected.length})
        </Button>
      </PageHeader>

      <Card className="p-4 bg-teal-50 dark:bg-teal-950/30 border-teal-200 dark:border-teal-900/50 mb-6">
        <div className="flex items-start gap-3 text-sm">
          <Mail className="h-5 w-5 text-teal-700 dark:text-teal-400 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-teal-900 dark:text-teal-300">E-posta gönderimi aktif</div>
            <p className="text-teal-800/80 dark:text-teal-400/80 mt-0.5">Veli e-postası tanımlı öğrencilere karne (PDF ekli) otomatik gönderilir. Karne; öğrencinin <strong>güçlü dersleri</strong>, <strong>geliştirmesi gereken dersleri (yanlış yaptığı sorularla)</strong> ve <strong>tekrar etmesi gereken konuları</strong> içerir. Göndermeden önce <strong>Önizle</strong> ile içeriği görebilirsiniz.</p>
          </div>
        </div>
      </Card>

      <Card className="p-5 bg-card border-border mb-6">
        <div className="space-y-1.5 max-w-md"><Label>Sınav</Label>
          <Select value={examId} onValueChange={setExamId}><SelectTrigger data-testid="pn-exam"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-card border-border">{exams.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent></Select>
        </div>
      </Card>

      {results.length === 0 ? <EmptyState icon={MessageSquareShare} title="Sonuç yok" desc="Bu sınav için sonuç bulunmuyor." /> : (
        <Card className="bg-card border-border overflow-hidden">
          <div className="p-3 border-b border-border flex items-center gap-2">
            <Checkbox checked={selected.length === results.length && results.length > 0} onCheckedChange={toggleAll} data-testid="pn-select-all" />
            <span className="text-sm text-muted-foreground">Tümünü seç</span>
          </div>
          <div className="divide-y divide-border">
            {results.map((r) => {
              const pemail = parentOf(r.student_id);
              const st = statuses[r.id];
              const p = preview[r.id];
              const sec = p?.sections || {};
              return (
                <div key={r.id} data-testid={`pn-item-${r.number}`}>
                <div className="p-3 flex items-center gap-3" data-testid={`pn-row-${r.number}`}>
                  <Checkbox checked={selected.includes(r.id)} onCheckedChange={() => toggle(r.id)} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{r.student_name} <Badge variant="secondary" className="ml-1">{r.grade}-{r.branch}</Badge></div>
                    <div className="text-xs text-muted-foreground font-mono">Net: {r.total_net} · Puan: {r.score}</div>
                    <div className="text-xs mt-0.5">{pemail ? <span className="text-muted-foreground truncate">{pemail}</span> : <span className="text-amber-600 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />E-posta yok</span>}</div>
                  </div>
                  {st === "sent" && <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 gap-1"><CheckCircle2 className="h-3 w-3" />Gönderildi</Badge>}
                  {st === "error" && <Badge className="bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">Hata</Badge>}
                  {st === "sending" && <Badge variant="secondary"><Loader2 className="h-3 w-3 animate-spin" /></Badge>}
                  <Button size="sm" variant="ghost" onClick={() => togglePreview(r.id)} data-testid={`pn-preview-btn-${r.number}`}>
                    {previewLoading === r.id ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Eye className="h-3.5 w-3.5 mr-1.5" />}{openId === r.id ? "Gizle" : "Önizle"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => sendOne(r.id)} disabled={!pemail || st === "sending"} data-testid={`pn-send-${r.number}`}>
                    <Send className="h-3.5 w-3.5 mr-1.5" />Gönder
                  </Button>
                </div>
                {openId === r.id && p && (
                  <div className="px-3 pb-4 pt-1 bg-muted/30 border-t border-border" data-testid={`pn-preview-${r.number}`}>
                    {p.parent_message && (
                      <div className="rounded-lg bg-teal-50 dark:bg-teal-950/30 border border-teal-200 dark:border-teal-900/50 p-3 text-xs text-teal-900 dark:text-teal-200 leading-relaxed mb-3">{p.parent_message}</div>
                    )}
                    {p.prayer_attendance?.prayers && (
                      <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/20 p-3 mb-3" data-testid={`pn-namaz-${r.number}`}>
                        <div className="text-[11px] font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wide mb-2">Namaz Yoklaması (bu hafta · toplam gelmedi)</div>
                        <div className="flex flex-wrap gap-1.5">
                          {["sabah", "ogle", "ikindi", "aksam", "yatsi"].map((k) => {
                            const v = p.prayer_attendance.prayers[k] || { week: 0, total: 0 };
                            const lbl = p.prayer_attendance.labels?.[k] || k;
                            return <span key={k} className="px-2 py-0.5 rounded-full bg-white dark:bg-amber-900/30 border border-amber-200 dark:border-amber-900/50 text-xs">{lbl} · hafta <span className={v.week ? "text-red-600 font-bold" : "text-emerald-600 font-bold"}>{v.week}</span> · toplam {v.total}</span>;
                          })}
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="rounded-lg border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-950/20 p-3">
                        <div className="flex items-center gap-1.5 text-[11px] font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-wide mb-2"><ThumbsUp className="h-3.5 w-3.5" />Güçlü Dersler</div>
                        {(sec.strong_subjects || []).length ? (
                          <div className="flex flex-wrap gap-1.5">
                            {sec.strong_subjects.map((s) => <span key={s.key} className="px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 text-xs font-semibold">{s.name} · net {s.net}</span>)}
                          </div>
                        ) : <div className="text-xs text-muted-foreground">—</div>}
                      </div>
                      <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/20 p-3">
                        <div className="flex items-center gap-1.5 text-[11px] font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wide mb-2"><AlertTriangle className="h-3.5 w-3.5" />Geliştirilmesi Gereken</div>
                        {(sec.weak_subjects || []).length ? (
                          <div className="space-y-1.5">
                            {sec.weak_subjects.map((s) => (
                              <div key={s.key} className="text-xs">
                                <span className="font-semibold text-amber-900 dark:text-amber-200">{s.name}</span> <span className="text-amber-700 dark:text-amber-400">({s.wrong} yanlış · net {s.net})</span>
                                {s.wrong_questions?.length ? <div className="text-[11px] text-amber-700/80 dark:text-amber-400/80 mt-0.5">Yanlış sorular: {s.wrong_questions.join(", ")}</div> : null}
                              </div>
                            ))}
                          </div>
                        ) : <div className="text-xs text-muted-foreground">Tüm derslerde başarılı 🎉</div>}
                      </div>
                      <div className="rounded-lg border border-blue-200 dark:border-blue-900/50 bg-blue-50 dark:bg-blue-950/20 p-3">
                        <div className="flex items-center gap-1.5 text-[11px] font-bold text-blue-700 dark:text-blue-400 uppercase tracking-wide mb-2"><BookOpen className="h-3.5 w-3.5" />Tekrar Konuları</div>
                        {(sec.review_topics || []).length ? (
                          <ul className="space-y-1">
                            {sec.review_topics.map((t, i) => <li key={i} className="text-xs text-blue-900 dark:text-blue-200"><span className="font-semibold">{t.subject_name}</span> — {t.topic} <span className="text-red-600 dark:text-red-400">({t.wrong})</span></li>)}
                          </ul>
                        ) : <div className="text-xs text-muted-foreground">Sorulara konu etiketi girilmemiş</div>}
                      </div>
                    </div>
                  </div>
                )}
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}
